import { useEffect, useRef, useState } from 'react';
import type { Subject } from '../lib/api/endpoints.js';
import { subjectDisplayName, subjectInitial, subjectMeta } from '../lib/subjects.js';
import { relativeTime } from '../lib/standing.js';

interface Props {
  subjects: Subject[];
  subjectId: string;
  /** Newest observation timestamp per subject, for ordering and the "2d ago" column. */
  lastObservedAtBySubject: Record<string, string>;
  onChange: (subjectId: string) => void;
}

/**
 * Choose the animal without leaving the flow.
 *
 * Replaces the read-only subject strip that used to sit inside the review
 * step. The whole point is speed: the animal recorded most often is one tap
 * away and no page change, and the roster is ordered by most recently
 * recorded so that tap is usually the first row.
 */
export function SubjectPicker({ subjects, subjectId, lastObservedAtBySubject, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const subject = subjects.find((s) => s.id === subjectId);

  const ordered = [...subjects].sort((a, b) => {
    const aAt = lastObservedAtBySubject[a.id];
    const bAt = lastObservedAtBySubject[b.id];
    if (aAt && bAt) return bAt.localeCompare(aAt);
    if (aAt) return -1;
    if (bAt) return 1;
    return 0;
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [open]);

  if (!subject) return null;

  const choose = (id: string) => {
    setOpen(false);
    if (id !== subjectId) onChange(id);
  };

  return (
    <div className="subject-picker" ref={rootRef}>
      <span className="subject-picker-label" id="subject-picker-label">
        Who's making the sound
      </span>

      <button
        type="button"
        className="subject-picker-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-describedby="subject-picker-label"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="subject-avatar" aria-hidden="true">
          {subjectInitial(subject)}
        </span>
        <span className="subject-picker-body">
          <span className="subject-picker-name">{subjectDisplayName(subject)}</span>
          <span className="subject-picker-meta">{subjectMeta(subject)}</span>
        </span>
        <span className="subject-picker-change" aria-hidden="true">
          Change ▾
        </span>
      </button>

      {open && (
        <div className="subject-picker-list" role="listbox" aria-label="Choose an animal">
          {ordered.map((s) => (
            <button
              key={s.id}
              type="button"
              role="option"
              aria-selected={s.id === subjectId}
              className={`subject-picker-option${s.id === subjectId ? ' subject-picker-option--on' : ''}`}
              onClick={() => choose(s.id)}
            >
              <span className="subject-avatar subject-avatar--sm" aria-hidden="true">
                {subjectInitial(s)}
              </span>
              <span className="subject-picker-body">
                <span className="subject-picker-option-name">{subjectDisplayName(s)}</span>
                <span className="subject-picker-meta">{subjectMeta(s)}</span>
              </span>
              <span className="subject-picker-last">{relativeTime(lastObservedAtBySubject[s.id])}</span>
            </button>
          ))}
          <button
            type="button"
            className="subject-picker-add"
            onClick={() => {
              setOpen(false);
              location.hash = '#/animals';
            }}
          >
            <span aria-hidden="true">+</span>
            Add an animal
          </button>
        </div>
      )}
    </div>
  );
}
